import { useCallback, useEffect, useRef, useState } from "react";
import { FiMessageSquare, FiRefreshCw, FiSearch, FiX } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
const statuses = [
  "OPEN",
  "IN_PROGRESS",
  "WAITING_FOR_CUSTOMER",
  "RESOLVED",
  "CLOSED",
];
const label = (value) =>
  value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
const date = (value) =>
  new Date(value).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
export default function SupportTicketsPage() {
  const { accessToken, user } = useAuth();
  const canManage = hasPermission(user, "customer_support.manage");
  const [search, setSearch] = useState(""),
    [status, setStatus] = useState(""),
    [filters, setFilters] = useState({ search: "", status: "" });
  const [page, setPage] = useState(1),
    [data, setData] = useState({ items: [], total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false),
    [error, setError] = useState("");
  const [selected, setSelected] = useState(null),
    [ticket, setTicket] = useState(null),
    [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState(""),
    [nextStatus, setNextStatus] = useState(""),
    [saving, setSaving] = useState(false),
    [detailError, setDetailError] = useState("");
  const listRevision = useRef(0),
    detailRevision = useRef(0),
    busy = useRef(false);
  const load = useCallback(async () => {
    const revision = ++listRevision.current;
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest(
        "/customer-support-admin/tickets?" +
          new URLSearchParams({ ...filters, page, take: 25 }),
        { accessToken },
      );
      if (revision === listRevision.current) setData(result);
    } catch (e) {
      if (revision === listRevision.current)
        setError(e.message || "Unable to load tickets.");
    } finally {
      if (revision === listRevision.current) setLoading(false);
    }
  }, [accessToken, filters, page]);
  useEffect(() => {
    void load();
  }, [load]);
  const loadDetail = useCallback(
    async (id) => {
      const revision = ++detailRevision.current;
      setDetailLoading(true);
      setDetailError("");
      try {
        const result = await apiRequest(
          "/customer-support-admin/tickets/" + id,
          { accessToken },
        );
        if (revision === detailRevision.current) {
          setTicket(result);
          setNextStatus(result.status);
        }
      } catch (e) {
        if (revision === detailRevision.current)
          setDetailError(e.message || "Unable to load ticket.");
      } finally {
        if (revision === detailRevision.current) setDetailLoading(false);
      }
    },
    [accessToken],
  );
  function open(id) {
    setSelected(id);
    setTicket(null);
    setReply("");
    void loadDetail(id);
  }
  function close() {
    if (saving) return;
    detailRevision.current++;
    setSelected(null);
    setTicket(null);
    setDetailError("");
  }
  async function save(event) {
    event.preventDefault();
    if (busy.current || !ticket) return;
    busy.current = true;
    setSaving(true);
    setDetailError("");
    try {
      await apiRequest("/customer-support-admin/tickets/" + selected, {
        method: "PATCH",
        accessToken,
        body: {
          ...(reply.trim() ? { message: reply.trim() } : {}),
          status:
            reply.trim() &&
            nextStatus === ticket.status &&
            !["CLOSED", "RESOLVED"].includes(nextStatus)
              ? "WAITING_FOR_CUSTOMER"
              : nextStatus,
        },
      });
      setReply("");
      await Promise.all([load(), loadDetail(selected)]);
    } catch (e) {
      setDetailError(e.message || "Unable to update ticket.");
    } finally {
      busy.current = false;
      setSaving(false);
    }
  }
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-primary-800">
            Customer care
          </p>
          <h1 className="mt-2 text-3xl font-bold text-slate-900">
            Support Tickets
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Manage the same conversations customers see in the CHARMS loyalty
            app.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 rounded-xl border bg-white px-4 py-3 text-sm"
        >
          <FiRefreshCw /> Refresh
        </button>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setFilters({ search: search.trim(), status });
        }}
        className="flex flex-wrap gap-3 rounded-2xl border bg-white p-5"
      >
        <input
          aria-label="Search tickets"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          maxLength={200}
          placeholder="Ticket, subject, customer or phone…"
          className="min-w-0 flex-1 rounded-xl border p-3"
        />
        <select
          aria-label="Ticket status filter"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className="rounded-xl border p-3"
        >
          <option value="">All statuses</option>
          {statuses.map((value) => (
            <option key={value} value={value}>
              {label(value)}
            </option>
          ))}
        </select>
        <button className="flex items-center gap-2 rounded-xl bg-primary-800 px-5 py-3 font-semibold text-white">
          <FiSearch /> Search
        </button>
      </form>
      {error && (
        <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-700">
          {error}
        </p>
      )}
      <div className="overflow-hidden rounded-2xl border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px] border-collapse text-sm">
            <thead className="bg-primary-800 text-white">
              <tr>
                {[
                  "Ticket",
                  "Customer",
                  "Subject",
                  "Status",
                  "Messages",
                  "Last activity",
                  "Actions",
                ].map((name) => (
                  <th
                    key={name}
                    className="border border-primary-900 p-4 text-left"
                  >
                    {name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-500">
                    Loading tickets…
                  </td>
                </tr>
              ) : (
                data.items.map((item) => (
                  <tr key={item.id} className="hover:bg-primary-50">
                    <td className="border p-4 font-semibold">
                      {item.ticketNumber}
                    </td>
                    <td className="border p-4">
                      {item.customer.fullName}
                      <p className="mt-1 text-xs text-slate-500">
                        {item.customer.phone || item.customer.email || "—"}
                      </p>
                    </td>
                    <td className="border p-4">{item.subject}</td>
                    <td className="border p-4">
                      <span className="rounded-lg bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-800">
                        {label(item.status)}
                      </span>
                    </td>
                    <td className="border p-4">{item._count.messages}</td>
                    <td className="border p-4">{date(item.lastMessageAt)}</td>
                    <td className="border p-4">
                      <button
                        onClick={() => open(item.id)}
                        className="flex items-center gap-2 font-semibold text-primary-800"
                      >
                        <FiMessageSquare /> Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
              {!loading && !data.items.length && (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-slate-500">
                    No tickets match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-4 border-t p-4 text-sm">
          <span>
            {data.total} tickets · Page {page} of {Math.max(1, data.totalPages)}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage((value) => value - 1)}
              className="rounded-lg border px-4 py-2 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              disabled={page >= data.totalPages || loading}
              onClick={() => setPage((value) => value + 1)}
              className="rounded-lg border px-4 py-2 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
      {selected && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Support ticket conversation"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
        >
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b p-5">
              <div>
                <h2 className="font-bold">
                  {ticket?.ticketNumber || "Support ticket"}
                </h2>
                <p className="mt-1 text-sm text-slate-500">{ticket?.subject}</p>
              </div>
              <div className="flex gap-3">
                <button
                  aria-label="Refresh conversation"
                  disabled={saving || detailLoading}
                  onClick={() => loadDetail(selected)}
                >
                  <FiRefreshCw />
                </button>
                <button
                  aria-label="Close conversation"
                  disabled={saving}
                  onClick={close}
                >
                  <FiX size={22} />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto p-5">
              {ticket?.category === 'ACCOUNT_DELETION' && <div className="my-3 rounded border border-amber-300 bg-amber-50 p-4 text-sm">Verified account-deletion request. Do not mark resolved merely by disabling the customer. Follow the approved data-erasure/retention process, revoke sessions, and confirm actual completion to the requester. Processing owner and response timeframe must be confirmed before release.</div>}
              {detailError && (
                <p
                  role="alert"
                  className="mb-4 rounded-xl bg-red-50 p-3 text-red-700"
                >
                  {detailError}
                </p>
              )}
              {detailLoading ? (
                <p>Loading conversation…</p>
              ) : (
                ticket && (
                  <>
                    <div className="mb-5 rounded-xl bg-slate-50 p-4 text-sm">
                      <p className="font-semibold">
                        {ticket.customer.fullName}
                      </p>
                      <p className="mt-1 text-slate-500">
                        {ticket.customer.email} · {ticket.customer.phone}
                      </p>
                      <p className="mt-2 font-semibold text-primary-800">
                        {label(ticket.status)}
                      </p>
                    </div>
                    <div className="space-y-3">
                      {ticket.messages.map((message) => (
                        <div
                          key={message.id}
                          className={
                            "rounded-xl border p-4 " +
                            (message.sender === "STAFF"
                              ? "border-primary-100 bg-primary-50"
                              : "bg-white")
                          }
                        >
                          <div className="mb-2 flex justify-between gap-3 text-xs text-slate-500">
                            <span className="font-bold">
                              {message.sender === "STAFF"
                                ? "CHARMS Support"
                                : message.sender === "CUSTOMER"
                                  ? ticket.customer.fullName
                                  : "System"}
                            </span>
                            <span>{date(message.createdAt)}</span>
                          </div>
                          <p className="whitespace-pre-wrap break-words text-sm leading-6">
                            {message.message}
                          </p>
                        </div>
                      ))}
                    </div>
                    {canManage ? (
                      <form
                        onSubmit={save}
                        className="mt-6 space-y-4 border-t pt-5"
                      >
                        <label className="block text-sm font-semibold">
                          Status
                          <select
                            disabled={saving}
                            value={nextStatus}
                            onChange={(event) =>
                              setNextStatus(event.target.value)
                            }
                            className="mt-2 w-full rounded-xl border p-3"
                          >
                            {statuses.map((value) => (
                              <option key={value} value={value}>
                                {label(value)}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block text-sm font-semibold">
                          Reply
                          <textarea
                            disabled={saving}
                            value={reply}
                            onChange={(event) => setReply(event.target.value)}
                            maxLength={5000}
                            rows={4}
                            placeholder="Reply to the customer. Reopen resolved/closed tickets before replying."
                            className="mt-2 w-full rounded-xl border p-3"
                          />
                        </label>
                        <button
                          disabled={
                            saving ||
                            (!reply.trim() && nextStatus === ticket.status)
                          }
                          className="rounded-xl bg-primary-800 px-5 py-3 font-semibold text-white disabled:opacity-40"
                        >
                          {saving
                            ? "Saving…"
                            : reply.trim()
                              ? "Send reply & update"
                              : "Update status"}
                        </button>
                      </form>
                    ) : (
                      <p className="mt-6 text-sm text-slate-500">
                        View-only access. Replying requires Customer Support
                        Manage permission.
                      </p>
                    )}
                  </>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
